import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
  getBinderWithCards, 
  getListCards, 
  removeCardFromList, 
  removeCardFromBinder,
  deleteBinder,
  updateCardQuantityInList,
  updateCardQuantityInBinder,
  getCardgames,
  invalidateFoldersStatsCache,
  getUserProfile,
  CARD_GAMES
} from '../../services/supabaseService';
import { motion, AnimatePresence } from 'motion/react';
import { Card, GameType } from '../../types';
import { CardImage } from '../../components/CardImage';
import { FolderCardDetailModal } from '../../components/member/FolderCardDetailModal';
import { ImportFolderModal } from '../../components/member/ImportFolderModal';
import { useSettings } from '../../contexts/SettingsContext';

interface BinderCard extends Card {
  dbId: any;
  quantity?: number;
  game_id?: any;
  price?: number;
  [key: string]: any;
}

const CARDS_PER_PAGE = 36;

/**
 * Retorna o código da carta limitado a 8 caracteres
 */
const getCardCodeDisplay = (card: BinderCard): string => {
  let raw = String(card.code || card.external_id || '').trim();
  // If the string is purely a small number (internal DB id), don't show it as code
  if (/^\d{1,4}$/.test(raw)) return '';
  if (raw.startsWith('#')) {
    raw = raw.slice(1).trim();
  }
  if (!raw) return '';
  return raw.slice(0, 10);
};

/**
 * Card renderizado em modo Grade (Grid)
 * Otimizado com proporções TCG perfeitas, badges legíveis e touch target no mobile
 */
const FolderCardItem = React.memo<{
  card: BinderCard;
  idx: number;
  canEdit: boolean;
  isSystem?: boolean;
  onOpenDetail: (card: BinderCard) => void;
  onDelete: (card: BinderCard) => void;
  onContactCard?: (card: BinderCard) => void;
  cardCode?: string;
  subtitle?: string;
}>(({ card, idx, canEdit, isSystem, onOpenDetail, onDelete, onContactCard, cardCode, subtitle }) => {
  const { settings, formatPrice } = useSettings();
  const quantity = typeof card.quantity === 'number' ? card.quantity : 1;
  const displayCode = cardCode || subtitle || getCardCodeDisplay(card);

  return (
    <div 
      id={`folder-card-${card.dbId || card.id || idx}`}
      onClick={() => onOpenDetail(card)}
      className="group relative bg-slate-900/80 hover:bg-slate-900 border border-slate-800/90 hover:border-purple-500/60 active:border-purple-500 rounded-xl sm:rounded-2xl p-2 sm:p-2.5 transition-all duration-200 shadow-sm hover:shadow-lg hover:shadow-purple-950/20 cursor-pointer flex flex-col justify-between active:scale-[0.98] select-none"
    >
      <div>
        {/* Imagem com proporção padrão TCG (2.5 / 3.5) */}
        <div className="aspect-[2.5/3.5] rounded-lg sm:rounded-xl overflow-hidden mb-2 bg-slate-950 relative shadow-inner border border-slate-800/60">
          <CardImage 
            src={card.imageUrl} 
            alt={card.name} 
            game={card.game}
            gameId={card.game_id}
            cardBackUrl={card.cardBackUrl || card.card_back_url}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
          />

          {/* Badge de Quantidade - Top Left (Apenas em pastas do sistema que suportam múltiplas cópias) */}
          {isSystem ? (
            <div className="absolute top-1.5 left-1.5 z-10 pointer-events-none">
              {quantity === 0 ? (
                <span className="bg-amber-500 text-slate-950 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md shadow font-mono">
                  0
                </span>
              ) : (
                <span className="bg-black/85 backdrop-blur-sm text-white text-[10px] sm:text-xs font-black px-1.5 sm:px-2 py-0.5 rounded-md sm:rounded-lg border border-white/15 shadow font-mono">
                  x{quantity}
                </span>
              )}
            </div>
          ) : null}

          {/* Botão de Excluir Rápido (Desktop Hover) quando canEdit */}
          {canEdit && (
            <div className="hidden sm:block absolute top-1.5 right-1.5 z-10">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(card);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-lg bg-black/80 hover:bg-red-600 text-slate-300 hover:text-white border border-white/10 hover:border-red-500 flex items-center justify-center shadow-lg active:scale-90 cursor-pointer"
                title="Remover da pasta"
              >
                <i className="fas fa-trash-can text-[10px]"></i>
              </button>
            </div>
          )}

          {/* Botão de Tenho Interesse / Contato no CardLink (Desktop Hover) quando !canEdit */}
          {!canEdit && onContactCard && (
            <div className="hidden sm:block absolute top-1.5 right-1.5 z-10">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onContactCard(card);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-lg bg-black/80 hover:bg-purple-600 text-purple-300 hover:text-white border border-purple-500/30 hover:border-purple-400 flex items-center justify-center shadow-lg active:scale-90 cursor-pointer"
                title="Tenho interesse nesta carta (CardLink)"
              >
                <i className="fas fa-paper-plane text-[10px]"></i>
              </button>
            </div>
          )}

          {/* Dica sutil no Desktop */}
          <div className="hidden sm:flex absolute inset-x-0 bottom-0 py-1.5 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center gap-1.5 text-[10px] font-bold text-slate-200 pointer-events-none z-10">
            <i className="fas fa-eye text-[10px] text-purple-400"></i>
            <span>Ver detalhes</span>
          </div>
        </div>

        {/* Informações da Carta */}
        <div className="px-0.5 space-y-1">
          <h4 className="text-xs sm:text-sm font-bold text-white truncate leading-tight group-hover:text-purple-300 transition-colors" title={card.name}>
            {card.name}
          </h4>
          <div className="flex items-center justify-between text-[10px] sm:text-xs gap-1.5">
            {displayCode ? (
              <span className="font-mono text-slate-400 font-semibold truncate max-w-[58%]" title={card.code || card.external_id}>
                {displayCode}
              </span>
            ) : (
              <span className="text-slate-600 font-mono truncate max-w-[58%]">—</span>
            )}
            {settings.showCardPrices && card.price !== undefined && card.price > 0 && (
              <span className="font-mono font-bold text-emerald-400 text-[10px] sm:text-xs shrink-0">
                {formatPrice((card.price || 0) * quantity)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Card renderizado em modo Lista (List)
 * Excelente para mobile: visualização compacta, controles de quantidade imediatos e preço claro
 */
const FolderCardListItem = React.memo<{
  card: BinderCard;
  idx: number;
  canEdit: boolean;
  isSystem?: boolean;
  onOpenDetail: (card: BinderCard) => void;
  onDelete: (card: BinderCard) => void;
  onUpdateQuantity: (card: BinderCard, delta: number) => void;
  onContactCard?: (card: BinderCard) => void;
  cardCode?: string;
  subtitle?: string;
}>(({ card, idx, canEdit, isSystem, onOpenDetail, onDelete, onUpdateQuantity, onContactCard, cardCode, subtitle }) => {
  const { settings, formatPrice } = useSettings();
  const quantity = typeof card.quantity === 'number' ? card.quantity : 1;
  const unitPrice = card.price || 0;
  const totalPrice = unitPrice * quantity;
  const displayCode = cardCode || subtitle || getCardCodeDisplay(card);

  return (
    <div 
      id={`folder-card-row-${card.dbId || card.id || idx}`}
      onClick={() => onOpenDetail(card)}
      className="group bg-slate-900/80 hover:bg-slate-900 border border-slate-800/90 hover:border-purple-500/60 rounded-xl sm:rounded-2xl p-2 sm:p-2.5 transition-all duration-200 flex items-center justify-between gap-2.5 sm:gap-4 cursor-pointer active:scale-[0.99] select-none shadow-sm"
    >
      <div className="flex items-center space-x-2.5 sm:space-x-3.5 min-w-0 flex-1">
        {/* Thumbnail da Carta */}
        <div className="w-10 sm:w-12 aspect-[2.5/3.5] rounded-lg overflow-hidden bg-slate-950 shrink-0 border border-slate-800 relative">
          <CardImage 
            src={card.imageUrl} 
            alt={card.name} 
            game={card.game}
            gameId={card.game_id}
            cardBackUrl={card.cardBackUrl || card.card_back_url}
            className="w-full h-full object-cover" 
          />
        </div>

        {/* Detalhes de Texto */}
        <div className="min-w-0 flex-1 space-y-0.5">
          <h4 className="text-xs sm:text-sm font-bold text-white truncate group-hover:text-purple-300 transition-colors" title={card.name}>
            {card.name}
          </h4>
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] sm:text-xs text-slate-400">
            {displayCode && (
              <span className="font-mono text-slate-400 bg-slate-950 px-1.5 py-0.5 rounded border border-slate-800 text-[9px] sm:text-[10px] font-semibold">
                {displayCode}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Controles Laterais: Preço, Quantidade e Excluir */}
      <div className="flex items-center space-x-2 sm:space-x-3 shrink-0" onClick={e => e.stopPropagation()}>
        {settings.showCardPrices && unitPrice > 0 && (
          <div className="text-right min-w-[65px] sm:min-w-[80px]">
            <div className="font-mono font-bold text-emerald-400 text-xs sm:text-sm">
              {formatPrice(totalPrice)}
            </div>
            {isSystem && quantity > 1 && (
              <div className="text-[9px] sm:text-[10px] text-slate-500 font-mono">
                {formatPrice(unitPrice)} un.
              </div>
            )}
          </div>
        )}

        {/* Controles de Quantidade (Coleção/Wishlist/Offerlist) ou Remoção Direta (Pastas personalizadas) */}
        {canEdit ? (
          isSystem ? (
            <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg p-0.5 shadow-inner">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateQuantity(card, -1);
                }}
                disabled={quantity <= 0}
                className="w-6 h-6 sm:w-7 sm:h-7 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent transition-all cursor-pointer"
                title="Diminuir quantidade"
              >
                <i className="fas fa-minus text-[9px] sm:text-[10px]"></i>
              </button>
              <span className="w-5 sm:w-7 text-center font-mono font-bold text-xs sm:text-sm text-white">
                {quantity}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateQuantity(card, 1);
                }}
                className="w-6 h-6 sm:w-7 sm:h-7 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-all cursor-pointer"
                title="Aumentar quantidade"
              >
                <i className="fas fa-plus text-[9px] sm:text-[10px]"></i>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(card);
              }}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-950 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-800 hover:border-red-500/40 flex items-center justify-center transition-all cursor-pointer shadow-sm"
              title="Remover da pasta"
            >
              <i className="fas fa-trash-can text-xs"></i>
            </button>
          )
        ) : (
          <div className="flex items-center space-x-1.5">
            <div className="bg-slate-950 border border-slate-800 px-2 py-1 rounded-lg font-mono text-xs font-bold text-slate-300">
              x{quantity}
            </div>
            {onContactCard && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onContactCard(card);
                }}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-950 hover:bg-purple-600 text-purple-400 hover:text-white border border-slate-800 hover:border-purple-500/50 flex items-center justify-center transition-all cursor-pointer"
                title="Tenho interesse nesta carta (CardLink)"
              >
                <i className="fas fa-paper-plane text-[10px]"></i>
              </button>
            )}
          </div>
        )}

        {/* Botão de Excluir */}
        {canEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(card);
            }}
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-950 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-800 hover:border-red-500/30 flex items-center justify-center transition-all cursor-pointer"
            title="Remover carta"
          >
            <i className="fas fa-trash-can text-xs"></i>
          </button>
        )}
      </div>
    </div>
  );
});

export const FolderDetails: React.FC = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('user');
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();
  const { settings, formatPrice } = useSettings();
  const navigate = useNavigate();

  const [folder, setFolder] = useState<any>(null);
  const [cards, setCards] = useState<BinderCard[]>([]);
  const [games, setGames] = useState<any[]>(CARD_GAMES as any[]);
  const [selectedGame, setSelectedGame] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'default' | 'name-asc' | 'name-desc' | 'price-desc' | 'price-asc' | 'qty-desc' | 'qty-asc'>('default');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeletingBinder, setIsDeletingBinder] = useState<boolean>(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState<boolean>(false);
  const [cardToDelete, setCardToDelete] = useState<BinderCard | null>(null);
  const [isDeletingCard, setIsDeletingCard] = useState<boolean>(false);
  const [selectedCardForDetail, setSelectedCardForDetail] = useState<BinderCard | null>(null);
  const [ownerProfile, setOwnerProfile] = useState<any>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);

  const isSystem = folderId === 'colecao' || folderId === 'wishlist' || folderId === 'offerlist';
  const isOfferlist = folderId === 'offerlist';
  const effectiveUserId = ownerId || currentUser?.id;
  const canEdit = !ownerId || ownerId === currentUser?.id?.toString();

  useEffect(() => {
    if (ownerId && !canEdit) {
      getUserProfile(ownerId).then(profile => {
        setOwnerProfile(profile);
      }).catch(err => {
        console.warn('Erro ao carregar perfil do dono da pasta:', err);
      });
    } else {
      setOwnerProfile(null);
    }
  }, [ownerId, canEdit]);

  const handleContactCard = (card: BinderCard) => {
    navigate('/cardlink', {
      state: {
        targetUser: ownerProfile || (ownerId ? { id: ownerId } : null),
        initialCard: card.name
      }
    });
  };

  const refreshFolderData = useCallback(async () => {
    if (!effectiveUserId || !folderId) return;

    setLoading(true);
    try {
      const gamesList = await getCardgames();
      setGames(gamesList || []);

      if (isSystem) {
        const listType = folderId === 'colecao' ? 'cards' : folderId as any;
        const titles: any = { colecao: 'Coleção', wishlist: 'Wishlist', offerlist: 'Offerlist (Trocas)' };
        const icons: any = { colecao: 'fa-box-archive', wishlist: 'fa-heart', offerlist: 'fa-right-left' };
        const colors: any = { colecao: 'bg-purple-600', wishlist: 'bg-pink-600', offerlist: 'bg-emerald-600' };

        setFolder({
          id: folderId,
          name: ownerId ? `${titles[folderId]}` : `${titles[folderId]}`,
          icon: icons[folderId],
          color: colors[folderId],
          isSystem: true
        });

        getListCards(effectiveUserId, listType, (data) => {
          const mappedCards: BinderCard[] = data.map(c => ({
            ...c,
            id: c.card_id,
            dbId: c.id,
            card_id: c.card_id,
            card_db_id: c.card_db_id || c.card_id,
            name: c.name,
            imageUrl: c.image_url,
            image_url: c.image_url,
            game: c.game as GameType,
            quantity: typeof c.quantity === 'number' ? c.quantity : (c.quantity !== undefined && c.quantity !== null ? Number(c.quantity) : 1),
            rarity: c.rarity || c.raridade,
            raridade: c.raridade || c.rarity,
            code: c.variant_code || c.set_code || c.external_id || '',
            external_id: c.external_id || c.code || '',
            set: c.set_code || c.set_name || c.edition || '',
            set_code: c.set_code || null,
            variant_number: c.variant_number ?? 1,
            variant_code: c.variant_code || null,
            variant_external_id: c.variant_external_id || null,
            tcgplayer_id: c.tcgplayer_id || null,
            is_foil: c.is_foil ?? false,
            game_id: c.game_id,
            price: typeof c.price === 'number' ? c.price : 0,
            price_updated_at: c.price_updated_at
          }));
          setCards(mappedCards);
          setLoading(false);
        });
      } else {
        const data = await getBinderWithCards(effectiveUserId, folderId);
        if (data) {
          setFolder({
            ...data,
            icon: 'fa-folder',
            color: 'bg-slate-800'
          });
          const mappedCards: BinderCard[] = (data.cards || []).map((c: any) => ({
            ...c,
            id: c.card_id,
            dbId: c.id,
            card_id: c.card_id,
            card_db_id: c.card_db_id || c.card_id,
            name: c.name,
            imageUrl: c.image_url,
            image_url: c.image_url,
            game: c.game as GameType,
            quantity: typeof c.quantity === 'number' ? c.quantity : (c.quantity !== undefined && c.quantity !== null ? Number(c.quantity) : 1),
            rarity: c.rarity || c.raridade,
            raridade: c.raridade || c.rarity,
            code: c.variant_code || c.set_code || c.external_id || '',
            external_id: c.external_id || c.code || '',
            set: c.set_code || c.set_name || c.edition || '',
            set_code: c.set_code || null,
            variant_number: c.variant_number ?? 1,
            variant_code: c.variant_code || null,
            variant_external_id: c.variant_external_id || null,
            tcgplayer_id: c.tcgplayer_id || null,
            is_foil: c.is_foil ?? false,
            game_id: c.game_id || data.game_id,
            price: typeof c.price === 'number' ? c.price : 0,
            price_updated_at: c.price_updated_at
          }));
          setCards(mappedCards);
        } else {
          navigate('/pastas');
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching folder details:', error);
      setLoading(false);
    }
  }, [effectiveUserId, folderId, isSystem, ownerId, navigate]);

  useEffect(() => {
    refreshFolderData();
  }, [refreshFolderData]);

  const totalEstimatedValue = useMemo(() => {
    return cards.reduce((sum, c) => {
      const qty = typeof c.quantity === 'number' ? c.quantity : 1;
      return sum + ((c.price || 0) * qty);
    }, 0);
  }, [cards]);

  const handleConfirmDeleteCard = async () => {
    if (!currentUser || !folderId || !cardToDelete) return;
    setIsDeletingCard(true);
    try {
      if (isSystem) {
        const listType = folderId === 'colecao' ? 'cards' : folderId as any;
        await removeCardFromList(currentUser.id, listType, cardToDelete.id);
      } else {
        await removeCardFromBinder(folderId, cardToDelete.dbId);
      }
      showNotification(`"${cardToDelete.name}" foi removida de "${folder.name}"`, 'info');
      invalidateFoldersStatsCache(currentUser.id);
      setCards(prev => prev.filter(c => c.dbId !== cardToDelete.dbId));
      if (selectedCardForDetail && (selectedCardForDetail.dbId === cardToDelete.dbId || selectedCardForDetail.id === cardToDelete.id)) {
        setSelectedCardForDetail(null);
      }
      setCardToDelete(null);
    } catch (error: any) {
      console.error('Error removing card:', error);
      showNotification(error.message || 'Erro ao remover carta', 'error');
    } finally {
      setIsDeletingCard(false);
    }
  };

  const handleCardMoved = (movedCard: any) => {
    if (currentUser) {
      invalidateFoldersStatsCache(currentUser.id);
    }
    setCards(prev => prev.filter(c => {
      if (movedCard?.dbId && c?.dbId) return c.dbId !== movedCard.dbId;
      if (movedCard?.id && c?.id) return c.id !== movedCard.id;
      return true;
    }));
    if (selectedCardForDetail && (selectedCardForDetail.dbId === movedCard?.dbId || selectedCardForDetail.id === movedCard?.id)) {
      setSelectedCardForDetail(null);
    }
  };

  const handleUpdateQuantity = async (card: BinderCard, delta: number) => {
    if (!currentUser || !folderId || !canEdit) return;
    
    const currentQuantity = typeof card.quantity === 'number' ? card.quantity : 1;
    const newQuantity = currentQuantity + delta;
    
    if (newQuantity < 0) {
      return;
    }

    setCards(prev => prev.map(c => 
      c.dbId === card.dbId ? { ...c, quantity: newQuantity } : c
    ));

    setSelectedCardForDetail(prev => {
      if (prev && (prev.dbId === card.dbId || prev.id === card.id)) {
        return { ...prev, quantity: newQuantity };
      }
      return prev;
    });

    try {
      if (isSystem) {
        const listType = folderId === 'colecao' ? 'cards' : folderId as any;
        await updateCardQuantityInList(currentUser.id, listType, card.id, newQuantity);
      } else {
        await updateCardQuantityInBinder(card.dbId, newQuantity);
      }
      invalidateFoldersStatsCache(currentUser.id);
    } catch (error: any) {
      console.error('Error updating quantity:', error);
      setCards(prev => prev.map(c => 
        c.dbId === card.dbId ? { ...c, quantity: currentQuantity } : c
      ));
      setSelectedCardForDetail(prev => {
        if (prev && (prev.dbId === card.dbId || prev.id === card.id)) {
          return { ...prev, quantity: currentQuantity };
        }
        return prev;
      });
      showNotification(error.message || 'Erro ao atualizar quantidade', 'error');
    }
  };

  const handleSetDirectQuantity = async (card: BinderCard, newQuantity: number) => {
    if (!currentUser || !folderId || !canEdit) return;
    if (isNaN(newQuantity) || newQuantity < 0) return;

    const currentQuantity = typeof card.quantity === 'number' ? card.quantity : 1;
    if (newQuantity === currentQuantity) return;

    setCards(prev => prev.map(c => 
      c.dbId === card.dbId ? { ...c, quantity: newQuantity } : c
    ));

    setSelectedCardForDetail(prev => {
      if (prev && (prev.dbId === card.dbId || prev.id === card.id)) {
        return { ...prev, quantity: newQuantity };
      }
      return prev;
    });

    try {
      if (isSystem) {
        const listType = folderId === 'colecao' ? 'cards' : folderId as any;
        await updateCardQuantityInList(currentUser.id, listType, card.id, newQuantity);
      } else {
        await updateCardQuantityInBinder(card.dbId, newQuantity);
      }
      invalidateFoldersStatsCache(currentUser.id);
    } catch (error: any) {
      console.error('Error setting quantity:', error);
      setCards(prev => prev.map(c => 
        c.dbId === card.dbId ? { ...c, quantity: currentQuantity } : c
      ));
      setSelectedCardForDetail(prev => {
        if (prev && (prev.dbId === card.dbId || prev.id === card.id)) {
          return { ...prev, quantity: currentQuantity };
        }
        return prev;
      });
      showNotification(error.message || 'Erro ao atualizar quantidade', 'error');
    }
  };

  const handleOpenCardDetail = (card: BinderCard) => {
    setSelectedCardForDetail(card);
  };

  const handleConfirmDeleteBinder = async () => {
    if (!currentUser || !folderId || isSystem) return;

    setIsDeletingBinder(true);
    try {
      await deleteBinder(currentUser.id, folderId);
      invalidateFoldersStatsCache(currentUser.id);
      showNotification(`Pasta "${folder.name}" excluída com sucesso`, 'info');
      setShowDeleteFolderModal(false);
      navigate('/pastas');
    } catch (error: any) {
      console.error('Error deleting binder:', error);
      showNotification(error.message || 'Erro ao excluir pasta', 'error');
      setIsDeletingBinder(false);
    }
  };

  const availableGames = useMemo(() => {
    const gameMap = new Map<string, { id: string | number; name: string }>();
    
    games.forEach(g => {
      gameMap.set(g.name.toLowerCase(), { id: g.id, name: g.name });
    });

    cards.forEach(c => {
      if (c.game && typeof c.game === 'string') {
        const key = c.game.toLowerCase();
        if (!gameMap.has(key)) {
          gameMap.set(key, { id: key, name: c.game });
        }
      }
    });

    const cardGames = Array.from(new Set(cards.map(c => String(c.game || '').toLowerCase()).filter(Boolean)));
    return cardGames.map(cg => {
      return gameMap.get(cg) || { id: cg, name: cg };
    });
  }, [cards, games]);

  // Filtro de cartas por Jogo, Busca e Ordenação
  const processedCards = useMemo(() => {
    let result = [...cards];

    // Filtro por jogo
    if (selectedGame !== 'all') {
      const filterGame = selectedGame.toLowerCase();
      const gameSlug = filterGame.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      result = result.filter(c => {
        const cardGame = String(c.game || '').toLowerCase();
        return cardGame === filterGame || cardGame === gameSlug;
      });
    }

    // Filtro de texto (busca rápida na pasta)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(c => {
        const name = (c.name || '').toLowerCase();
        const code = (c.code || c.external_id || '').toLowerCase();
        const set = typeof c.set === 'string' ? c.set.toLowerCase() : ((c.set as any)?.name || '').toLowerCase();
        const rarity = (c.rarity || '').toLowerCase();
        return name.includes(q) || code.includes(q) || set.includes(q) || rarity.includes(q);
      });
    }

    // Ordenação
    if (sortBy === 'name-asc') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'name-desc') {
      result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy === 'price-desc') {
      result.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (sortBy === 'price-asc') {
      result.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === 'qty-desc') {
      result.sort((a, b) => (b.quantity || 1) - (a.quantity || 1));
    } else if (sortBy === 'qty-asc') {
      result.sort((a, b) => (a.quantity || 1) - (b.quantity || 1));
    }

    return result;
  }, [cards, selectedGame, searchQuery, sortBy]);

  // Reset de página ao alterar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGame, searchQuery, sortBy, folderId]);

  const totalPages = Math.ceil(processedCards.length / CARDS_PER_PAGE);
  const paginatedCards = useMemo(() => {
    if (processedCards.length <= CARDS_PER_PAGE) return processedCards;
    const start = (currentPage - 1) * CARDS_PER_PAGE;
    return processedCards.slice(start, start + CARDS_PER_PAGE);
  }, [processedCards, currentPage]);

  const getCardSubtitle = (card: BinderCard) => {
    return getCardCodeDisplay(card);
  };

  const handleExportCsv = () => {
    const listToExport = processedCards;
    if (!listToExport || listToExport.length === 0) {
      showNotification('Esta pasta não possui cartas para exportar com os filtros atuais.', 'info');
      return;
    }

    try {
      const escapeCsv = (val: any): string => {
        if (val === null || val === undefined) return '""';
        const str = String(val).trim();
        return `"${str.replace(/"/g, '""')}"`;
      };

      const headers = [
        'Id',
        'Nome',
        'Quantidade',
        'Jogo',
        'Código',
        'Coleção',
        'Raridade',
      ];

      const rows = listToExport.map(card => {
        const qty = typeof card.quantity === 'number' ? card.quantity : 1;
        let setName = '';
        if (typeof card.set === 'object' && card.set) {
          setName = (card.set as any)?.name || (card.set as any)?.beauty_name || (card.set as any)?.set_code || '';
        } else if (typeof card.set === 'string' && card.set.trim()) {
          setName = card.set.trim();
        }

        return [
          escapeCsv(card.external_id || ''),
          escapeCsv(card.name || ''),
          escapeCsv(qty),
          escapeCsv(card.game || ''),
          escapeCsv(card.code || ''),
          escapeCsv(setName || ''),
          escapeCsv(card.rarity || '')
        ];
      });

      const bom = '\uFEFF';
      const csvContent = bom + [
        headers.map(escapeCsv).join(','),
        ...rows.map(row => row.join(','))
      ].join('\r\n');

      const folderSlug = (folder?.name || 'pasta')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '');

      const gameSuffix = (selectedGame && selectedGame !== 'all')
        ? `_${selectedGame.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9_-]+/g, '_')}`
        : '';

      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `${folderSlug || 'pasta'}${gameSuffix}_${dateStr}.csv`;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotification(`Lista de cartas exportada com sucesso (${listToExport.length} itens)!`, 'success');
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
      showNotification('Erro ao exportar cartas para CSV.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!folder) return null;

  return (
    <div className="max-w-6xl 2xl:max-w-[1700px] mx-auto space-y-5 sm:space-y-6 md:space-y-8 animate-in fade-in duration-500 pb-24">
      {/* Header Compacto e Responsivo */}
      <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-3.5 sm:p-5 md:p-6 backdrop-blur-sm shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <Link 
              to={ownerId ? `/pastas?user=${ownerId}` : "/pastas"} 
              className="w-9 h-9 sm:w-11 sm:h-11 bg-slate-950 hover:bg-slate-800 border border-slate-800 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-colors shrink-0 shadow-sm active:scale-95"
              title={ownerId ? `Voltar para pastas de @${ownerProfile?.username || 'colecionador'}` : "Voltar para Pastas"}
            >
              <i className="fas fa-arrow-left text-xs sm:text-sm"></i>
            </Link>

            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl ${folder.color} flex items-center justify-center text-white text-base sm:text-xl shadow-md shrink-0`}>
              <i className={`fas ${folder.icon}`}></i>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-white uppercase tracking-tight leading-tight truncate">
                  {folder.name}
                </h1>
                {!canEdit && (
                  <span className="text-[9px] font-black uppercase tracking-wider bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full border border-purple-500/30 shrink-0">
                    Visualização
                  </span>
                )}
              </div>
              {ownerId ? (
                <div className="flex items-center gap-2 mt-1">
                  <Link 
                    to={`/perfil/${ownerId}`} 
                    className="text-[11px] sm:text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1.5 transition-colors"
                  >
                    {ownerProfile?.avatar ? (
                      <img src={ownerProfile.avatar} alt="" className="w-4 h-4 rounded-full object-cover border border-purple-500/40" />
                    ) : (
                      <i className="fas fa-user-circle text-xs"></i>
                    )}
                    <span>@{ownerProfile?.username || 'colecionador'}</span>
                  </Link>
                  <span className="text-slate-600 text-[10px]">•</span>
                  <Link 
                    to={`/pastas?user=${ownerId}`}
                    className="text-[10px] text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    Ver todas as pastas
                  </Link>
                </div>
              ) : (
                <p className="text-[11px] sm:text-xs text-slate-400 truncate">
                  {isSystem ? 'Pasta do Sistema' : (folder?.game_name || 'Pasta Pessoal')}
                </p>
              )}
            </div>
          </div>

          {/* Botões de Ação do Cabeçalho */}
          <div className="flex items-center flex-wrap gap-2 pt-2 sm:pt-0 border-t border-slate-800/60 sm:border-0 justify-end">
            {canEdit ? (
              <>
                <Link
                  to="/busca"
                  className="bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 transition-all shadow-sm active:scale-95"
                >
                  <i className="fas fa-plus text-[10px] text-purple-400"></i>
                  <span>Adicionar carta</span>
                </Link>

                <button 
                  type="button"
                  id="import-folder-button"
                  onClick={() => setIsImportModalOpen(true)}
                  className="bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 transition-all shadow-sm active:scale-95 cursor-pointer"
                  title="Importar lista de cartas para esta pasta"
                >
                  <i className="fas fa-file-import text-[10px] text-purple-400"></i>
                  <span>Importar</span>
                </button>

                <button 
                  type="button"
                  id="export-folder-csv-button"
                  onClick={handleExportCsv}
                  disabled={cards.length === 0}
                  className="bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 transition-all shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  title="Exportar cartas em CSV"
                >
                  <i className="fas fa-file-arrow-down text-[10px] text-emerald-400"></i>
                  <span>Exportar</span>
                </button>

                {!isSystem && (
                  <button 
                    type="button"
                    id="delete-folder-button"
                    onClick={() => setShowDeleteFolderModal(true)}
                    disabled={isDeletingBinder}
                    className="bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white border border-red-500/20 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 transition-all shadow-sm active:scale-95 cursor-pointer"
                    title="Excluir pasta"
                  >
                    <i className="fas fa-trash-can text-[10px]"></i>
                    <span className="hidden sm:inline">Excluir Pasta</span>
                  </button>
                )}
              </>
            ) : (
              <>
                {isOfferlist && (
                  <button
                    type="button"
                    onClick={() => navigate('/cardlink', { 
                      state: { 
                        targetUser: ownerProfile || (ownerId ? { id: ownerId } : null) 
                      } 
                    })}
                    className="bg-purple-600 hover:bg-purple-500 text-white font-black px-4 py-2 sm:py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-lg shadow-purple-600/20 flex items-center gap-2 active:scale-95 cursor-pointer"
                    title="Solicitar contato com este usuário no CardLink"
                  >
                    <i className="fas fa-paper-plane text-xs"></i>
                    <span>Solicitar Contato</span>
                  </button>
                )}

                <button 
                  type="button"
                  id="export-folder-csv-button"
                  onClick={handleExportCsv}
                  disabled={cards.length === 0}
                  className="bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 transition-all shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  title="Exportar cartas em CSV"
                >
                  <i className="fas fa-file-arrow-down text-[10px] text-emerald-400"></i>
                  <span>Exportar</span>
                </button>

                <Link
                  to={`/pastas?user=${ownerId}`}
                  className="bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl font-bold text-xs flex items-center space-x-1.5 transition-all shadow-sm active:scale-95"
                  title="Ver todas as pastas deste colecionador"
                >
                  <i className="fas fa-folder text-[10px] text-purple-400"></i>
                  <span className="hidden sm:inline">Todas as Pastas</span>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Linha de Estatísticas Rápidas (Especialmente clara no Mobile) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 pt-1">
          <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-2.5 sm:p-3 flex items-center space-x-2.5">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center shrink-0 text-xs sm:text-sm">
              <i className="fas fa-layer-group"></i>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-slate-400 uppercase font-black tracking-wider truncate">Total de Cartas</p>
              <p className="text-sm sm:text-base font-black text-white font-mono">
                {cards.reduce((sum, c) => sum + (typeof c.quantity === 'number' ? c.quantity : 1), 0)}
                <span className="text-[10px] font-normal text-slate-400 ml-1">({cards.length} únicas)</span>
              </p>
            </div>
          </div>

          {settings.showCardPrices ? (
            <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-2.5 sm:p-3 flex items-center space-x-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 text-xs sm:text-sm">
                <i className="fas fa-coins"></i>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-wider truncate">Valor Estimado</p>
                <p className="text-sm sm:text-base font-black text-emerald-400 font-mono truncate">
                  {formatPrice(totalEstimatedValue)}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-2.5 sm:p-3 flex items-center space-x-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center shrink-0 text-xs sm:text-sm">
                <i className="fas fa-clone"></i>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-wider truncate">Itens Únicos</p>
                <p className="text-sm sm:text-base font-black text-white font-mono">{cards.length}</p>
              </div>
            </div>
          )}

          <div className="col-span-2 sm:col-span-1 bg-slate-950/70 border border-slate-800/80 rounded-xl p-2.5 sm:p-3 flex items-center space-x-2.5">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center shrink-0 text-xs sm:text-sm">
              <i className="fas fa-gamepad"></i>
            </div>
            <div className="min-w-0 flex-1 truncate">
              <p className="text-[10px] text-slate-400 uppercase font-black tracking-wider truncate">Jogos na Pasta</p>
              <p className="text-xs sm:text-sm font-bold text-slate-200 truncate">
                {availableGames.length > 0 
                  ? `${availableGames.length} ${availableGames.length === 1 ? 'jogo' : 'jogos'}`
                  : (folder?.game_name || 'Geral')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Filtros, Busca Rápida, Ordenação e Alternador de Visão (Grade vs Lista) */}
      <div className="space-y-3">
        {/* Chips de Jogos (se houver mais de 1 jogo presente na pasta) */}
        {availableGames.length > 1 && (
          <div className="flex items-center space-x-1.5 sm:space-x-2 overflow-x-auto pb-1 no-scrollbar">
            <button
              onClick={() => setSelectedGame('all')}
              className={`px-3 py-1.5 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-wider transition-all border shrink-0 cursor-pointer ${
                selectedGame === 'all' 
                  ? 'bg-purple-600 border-purple-500 text-white shadow-md' 
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              Todos os Jogos
            </button>
            {availableGames.map(game => (
              <button
                key={game.id}
                onClick={() => setSelectedGame(game.name)}
                className={`px-3 py-1.5 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-wider transition-all border shrink-0 cursor-pointer ${
                  selectedGame === game.name 
                    ? 'bg-purple-600 border-purple-500 text-white shadow-md' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                {game.name}
              </button>
            ))}
          </div>
        )}

        {/* Linha de Busca, Ordenação e Modo Grade/Lista */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
          {/* Campo de Busca Rápida */}
          <div className="relative flex-1">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar nesta pasta por nome, código ou coleção..."
              className="w-full bg-slate-900/80 border border-slate-800 rounded-xl pl-9 pr-8 py-2 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer"
                title="Limpar busca"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Seletor de Ordenação */}
            <div className="relative flex-1 sm:flex-initial">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full sm:w-auto bg-slate-900/80 border border-slate-800 text-slate-300 text-xs rounded-xl px-3 py-2 pr-7 focus:outline-none focus:border-purple-500 cursor-pointer appearance-none"
              >
                <option value="default">Ordem Padrão</option>
                <option value="name-asc">Nome (A - Z)</option>
                <option value="name-desc">Nome (Z - A)</option>
                {settings.showCardPrices && (
                  <>
                    <option value="price-desc">Maior Preço</option>
                    <option value="price-asc">Menor Preço</option>
                  </>
                )}
                <option value="qty-desc">Maior Quantidade</option>
                <option value="qty-asc">Menor Quantidade</option>
              </select>
              <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none"></i>
            </div>

            {/* Alternador de Modo de Visualização */}
            <div className="flex items-center bg-slate-900/80 border border-slate-800 rounded-xl p-0.5 shrink-0">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs transition-all cursor-pointer ${
                  viewMode === 'grid'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                title="Visualização em Grade"
              >
                <i className="fas fa-grip"></i>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs transition-all cursor-pointer ${
                  viewMode === 'list'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                title="Visualização em Lista"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Renderização das Cartas (Grade ou Lista) */}
      {paginatedCards.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2.5 sm:gap-3 md:gap-4 lg:gap-5">
            {paginatedCards.map((card, idx) => (
              <FolderCardItem
                key={card.dbId || `${card.id}-${idx}`}
                card={card}
                idx={idx}
                canEdit={canEdit}
                isSystem={isSystem}
                onOpenDetail={handleOpenCardDetail}
                onDelete={setCardToDelete}
                onContactCard={!canEdit && isOfferlist ? handleContactCard : undefined}
                cardCode={getCardCodeDisplay(card)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {paginatedCards.map((card, idx) => (
              <FolderCardListItem
                key={card.dbId || `${card.id}-${idx}`}
                card={card}
                idx={idx}
                canEdit={canEdit}
                isSystem={isSystem}
                onOpenDetail={handleOpenCardDetail}
                onDelete={setCardToDelete}
                onUpdateQuantity={handleUpdateQuantity}
                onContactCard={!canEdit && isOfferlist ? handleContactCard : undefined}
                cardCode={getCardCodeDisplay(card)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="py-16 sm:py-20 text-center space-y-4 bg-slate-900/30 border border-slate-800/60 rounded-3xl p-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-900 rounded-full flex items-center justify-center text-slate-700 mx-auto">
            <i className="fas fa-box-open text-2xl sm:text-3xl"></i>
          </div>
          <div className="space-y-1">
            <p className="text-slate-300 font-bold uppercase text-xs tracking-widest">
              {searchQuery ? 'Nenhuma carta encontrada' : 'Nada por aqui'}
            </p>
            <p className="text-slate-500 text-xs sm:text-sm max-w-md mx-auto">
              {searchQuery 
                ? `Nenhum resultado corresponde à busca "${searchQuery}". Tente outro termo.`
                : canEdit
                  ? 'Esta pasta está vazia. Adicione cartas através da busca ou de outras coleções.'
                  : 'Esta pasta está vazia no momento.'}
            </p>
          </div>
          {searchQuery ? (
            <button 
              type="button"
              onClick={() => setSearchQuery('')}
              className="inline-block bg-slate-800 hover:bg-slate-700 text-white px-5 py-2 rounded-xl text-xs font-bold transition-all mt-2 cursor-pointer"
            >
              Limpar Busca
            </button>
          ) : canEdit ? (
            <Link 
              to="/busca" 
              className="inline-block bg-purple-600 hover:bg-purple-500 text-white px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all mt-2 shadow-lg shadow-purple-600/30"
            >
              Buscar Cartas
            </Link>
          ) : ownerId ? (
            <Link 
              to={`/pastas?user=${ownerId}`} 
              className="inline-block bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all mt-2 border border-slate-700 shadow-lg"
            >
              Ver Outras Pastas do Colecionador
            </Link>
          ) : null}
        </div>
      )}

      {/* Controles de Paginação (Totalmente amigáveis no Mobile) */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 pb-2">
          <div className="text-xs text-slate-400 font-medium order-2 sm:order-1">
            Exibindo <span className="text-white font-bold">{paginatedCards.length}</span> de <span className="text-white font-bold">{processedCards.length}</span> cartas
          </div>

          <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-between sm:justify-end">
            <button
              onClick={() => {
                setCurrentPage(p => Math.max(1, p - 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === 1}
              className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 hover:text-white hover:border-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center space-x-1.5 cursor-pointer shadow-sm"
            >
              <i className="fas fa-chevron-left text-[10px]"></i>
              <span>Anterior</span>
            </button>

            <div className="px-3.5 py-2 bg-slate-900/80 border border-slate-800 rounded-xl text-xs font-bold text-slate-400">
              <span className="text-white font-black">{currentPage}</span> / <span className="text-slate-300 font-bold">{totalPages}</span>
            </div>

            <button
              onClick={() => {
                setCurrentPage(p => Math.min(totalPages, p + 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === totalPages}
              className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 hover:text-white hover:border-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center space-x-1.5 cursor-pointer shadow-sm"
            >
              <span>Próxima</span>
              <i className="fas fa-chevron-right text-[10px]"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modal Dedicado de Detalhes e Gestão da Carta na Pasta */}
      {selectedCardForDetail && (
        <FolderCardDetailModal
          card={selectedCardForDetail}
          folder={folder}
          canEdit={canEdit}
          onClose={() => setSelectedCardForDetail(null)}
          cardsList={processedCards}
          onSelectCard={(c) => handleOpenCardDetail(c)}
          onUpdateQuantity={(c, delta) => handleUpdateQuantity(c, delta)}
          onSetDirectQuantity={(c, qty) => handleSetDirectQuantity(c, qty)}
          onDeleteCard={(c) => handleCardMoved(c)}
          onCardMoved={(c) => handleCardMoved(c)}
          gameName={folder?.game_name || (typeof folder?.game === 'string' ? folder?.game : undefined)}
          ownerProfile={ownerProfile}
          ownerId={ownerId}
        />
      )}

      {/* Modal de Confirmação de Exclusão de Carta */}
      <AnimatePresence>
        {cardToDelete && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-3.5 sm:p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-8 space-y-5 sm:space-y-6 shadow-2xl relative"
            >
              <button
                onClick={() => !isDeletingCard && setCardToDelete(null)}
                disabled={isDeletingCard}
                className="absolute top-5 right-5 sm:top-6 sm:right-6 text-slate-500 hover:text-white transition-colors disabled:opacity-40"
              >
                <i className="fas fa-times text-base sm:text-lg"></i>
              </button>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center text-lg shrink-0">
                  <i className="fas fa-trash-can"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-white">Remover Carta</h3>
                  <p className="text-xs text-slate-400">Confirmação de exclusão</p>
                </div>
              </div>

              {/* Pré-visualização da carta */}
              <div className="flex items-center space-x-3 sm:space-x-4 p-3 rounded-xl sm:rounded-2xl bg-slate-950/80 border border-slate-800/80">
                <div className="w-12 sm:w-14 aspect-[2.5/3.5] rounded-lg overflow-hidden bg-slate-900 shrink-0 border border-slate-800">
                  <CardImage
                    src={cardToDelete.imageUrl}
                    alt={cardToDelete.name}
                    game={cardToDelete.game}
                    gameId={cardToDelete.game_id}
                    cardBackUrl={cardToDelete.cardBackUrl || cardToDelete.card_back_url}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-0.5 sm:space-y-1 min-w-0 flex-1">
                  <h4 className="text-xs sm:text-sm font-bold text-white truncate">{cardToDelete.name}</h4>
                  <p className="text-[11px] text-slate-400">
                    Pasta: <span className="text-purple-400 font-semibold">{folder.name}</span>
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono">
                    Quantidade atual: <span className="text-white font-bold">{typeof cardToDelete.quantity === 'number' ? cardToDelete.quantity : 1}</span>
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                Tem certeza que deseja remover esta carta da pasta? Ela será retirada desta listagem.
              </p>

              <div className="flex items-center space-x-3 pt-1">
                <button
                  type="button"
                  onClick={() => setCardToDelete(null)}
                  disabled={isDeletingCard}
                  className="w-1/2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 font-bold py-2.5 sm:py-3 rounded-xl transition-all text-xs uppercase tracking-widest cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteCard}
                  disabled={isDeletingCard}
                  className="w-1/2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black py-2.5 sm:py-3 rounded-xl shadow-lg shadow-red-600/30 transition-all text-xs uppercase tracking-widest flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {isDeletingCard ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      <span>Removendo...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-can text-xs"></i>
                      <span>Sim, Excluir</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Confirmação de Exclusão de Pasta */}
      <AnimatePresence>
        {showDeleteFolderModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5 sm:p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-8 space-y-5 sm:space-y-6 shadow-2xl relative"
            >
              <button
                type="button"
                onClick={() => !isDeletingBinder && setShowDeleteFolderModal(false)}
                disabled={isDeletingBinder}
                className="absolute top-5 right-5 sm:top-6 sm:right-6 text-slate-500 hover:text-white transition-colors disabled:opacity-40 cursor-pointer"
              >
                <i className="fas fa-times text-base sm:text-lg"></i>
              </button>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center text-lg sm:text-xl shrink-0">
                  <i className="fas fa-trash-can"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-white">Excluir Pasta</h3>
                  <p className="text-xs text-slate-400">Esta ação é permanente</p>
                </div>
              </div>

              <div className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-slate-950/80 border border-slate-800/80 space-y-1">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-wider">Pasta selecionada</p>
                <h4 className="text-sm sm:text-base font-bold text-white">{folder.name}</h4>
                <p className="text-xs text-slate-500">
                  {cards.length} {cards.length === 1 ? 'carta vinculada' : 'cartas vinculadas'}
                </p>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                Tem certeza que deseja excluir esta pasta personalizada? As cartas continuarão na sua coleção principal, mas a pasta e sua organização serão removidas permanentemente.
              </p>

              <div className="flex items-center space-x-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowDeleteFolderModal(false)}
                  disabled={isDeletingBinder}
                  className="w-1/2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 font-bold py-2.5 sm:py-3 rounded-xl transition-all text-xs uppercase tracking-widest cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteBinder}
                  disabled={isDeletingBinder}
                  className="w-1/2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black py-2.5 sm:py-3 rounded-xl shadow-lg shadow-red-600/30 transition-all text-xs uppercase tracking-widest flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {isDeletingBinder ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-can text-xs"></i>
                      <span>Sim, Excluir</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Importação de Cartas na Pasta */}
      {canEdit && (
        <ImportFolderModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          folder={folder}
          folderId={folderId || ''}
          isSystem={isSystem}
          games={games}
          userId={currentUser?.id || ''}
          onSuccess={() => {
            refreshFolderData();
          }}
        />
      )}
    </div>
  );
};
